import React, { useState, useEffect } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import type {
  TodoTemplate,
  CreateTodoTemplateRequest,
  UpdateTodoTemplateRequest,
  TodoDependencyType,
} from "../../../../domain/types/todo";
import type { UserInfoResponse } from "../../../../shared/types/api";

interface TodoTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (request: CreateTodoTemplateRequest | UpdateTodoTemplateRequest) => Promise<void>;
  template?: TodoTemplate | null;
  existingTemplates: TodoTemplate[];
  companyUsers: UserInfoResponse[];
  isLoading?: boolean;
}

export const TodoTemplateDialog: React.FC<TodoTemplateDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  template,
  existingTemplates,
  companyUsers,
  isLoading = false,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [estimatedMinutes, setEstimatedMinutes] = useState(60);
  const [isActive, setIsActive] = useState(true);
  const [selectedAssignees, setSelectedAssignees] = useState<number[]>([]);
  const [dependencies, setDependencies] = useState<
    { dependsOnTemplateId: number; dependencyType: TodoDependencyType }[]
  >([]);

  const isEdit = !!template;

  // Initialize form when template changes
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || "");
      setEstimatedMinutes(template.estimatedMinutes);
      setIsActive(template.isActive);
      setSelectedAssignees(template.defaultAssignees.map((a) => a.userId));
      setDependencies(
        template.dependencies.map((d) => ({
          dependsOnTemplateId: d.dependsOnTemplateId,
          dependencyType: d.dependencyType,
        }))
      );
    } else {
      resetForm();
    }
  }, [template, isOpen]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setEstimatedMinutes(60);
    setIsActive(true);
    setSelectedAssignees([]);
    setDependencies([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const request: CreateTodoTemplateRequest | UpdateTodoTemplateRequest = {
      name,
      description: description || null,
      estimatedMinutes,
      isActive,
      defaultAssigneeUserIds: selectedAssignees.length > 0 ? selectedAssignees : undefined,
      dependencies: dependencies.length > 0 ? dependencies : undefined,
    };

    await onSubmit(request);
  };

  const handleAddDependency = () => {
    const availableTemplates = existingTemplates.filter(
      (t) =>
        t.id !== template?.id &&
        !dependencies.some((d) => d.dependsOnTemplateId === t.id)
    );

    if (availableTemplates.length > 0) {
      setDependencies([
        ...dependencies,
        {
          dependsOnTemplateId: availableTemplates[0].id,
          dependencyType: "WARNING",
        },
      ]);
    }
  };

  const handleRemoveDependency = (index: number) => {
    setDependencies(dependencies.filter((_, i) => i !== index));
  };

  const handleUpdateDependency = (
    index: number,
    field: "dependsOnTemplateId" | "dependencyType",
    value: number | TodoDependencyType
  ) => {
    const updated = [...dependencies];
    updated[index] = { ...updated[index], [field]: value };
    setDependencies(updated);
  };

  const toggleAssignee = (userId: number) => {
    if (selectedAssignees.includes(userId)) {
      setSelectedAssignees(selectedAssignees.filter((id) => id !== userId));
    } else {
      setSelectedAssignees([...selectedAssignees, userId]);
    }
  };

  const availableTemplatesForDependency = existingTemplates.filter(
    (t) => t.id !== template?.id
  );

  const eligibleUsers = companyUsers.filter(
    (u) => u.role === "COMPANY_ADMIN" || u.role === "COMPANY_MANAGER"
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Dialog */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? "작업 공정 수정" : "작업 공정 추가"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                작업명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="예: 로고 조판"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                설명
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="작업에 대한 설명을 입력하세요"
              />
            </div>

            {/* Estimated Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                예상 소요 시간 (분) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={1}
                value={estimatedMinutes}
                onChange={(e) => setEstimatedMinutes(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                활성화 (새 주문에 자동 적용)
              </label>
            </div>

            {/* Default Assignees */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                기본 담당자
              </label>
              {eligibleUsers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {eligibleUsers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => toggleAssignee(user.id)}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                        selectedAssignees.includes(user.id)
                          ? "bg-blue-50 border-blue-500 text-blue-700"
                          : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                      }`}
                    >
                      {user.name || user.email}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  배정 가능한 사용자가 없습니다.
                </p>
              )}
            </div>

            {/* Dependencies */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  선행 작업 의존성
                </label>
                <button
                  type="button"
                  onClick={handleAddDependency}
                  disabled={
                    availableTemplatesForDependency.length === 0 ||
                    dependencies.length >= availableTemplatesForDependency.length
                  }
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <Plus size={16} />
                  추가
                </button>
              </div>

              {dependencies.length > 0 ? (
                <div className="space-y-2">
                  {dependencies.map((dep, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <select
                        value={dep.dependsOnTemplateId}
                        onChange={(e) =>
                          handleUpdateDependency(
                            index,
                            "dependsOnTemplateId",
                            Number(e.target.value)
                          )
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {availableTemplatesForDependency.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                      <select
                        value={dep.dependencyType}
                        onChange={(e) =>
                          handleUpdateDependency(
                            index,
                            "dependencyType",
                            e.target.value as TodoDependencyType
                          )
                        }
                        className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="BLOCKING">차단</option>
                        <option value="WARNING">경고</option>
                        <option value="NOTIFICATION">알림</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => handleRemoveDependency(index)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  선행 작업이 없습니다.
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                차단: 선행 작업 완료 전까지 시작 불가 / 경고: 확인 후 진행 가능 / 알림: 알림만 전송
              </p>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isLoading}
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading || !name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isLoading ? "저장 중..." : isEdit ? "수정" : "추가"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TodoTemplateDialog;
